import { apiFetch } from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BlogStatus = 'published' | 'draft' | 'archived';

export type Section =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'callout'; text: string }
  | { type: 'divider' };

export interface Blog {
  id: number;
  slug: string;
  subtitle?: string;
  date?: string;
  readTime?: string;
  coverGradient?: string;
  body: Section[] | string;
  status: BlogStatus;
  createdAt: string;
  updatedAt: string;
  seoTitle?: string;
  seoDescription?: string;
  coverImageUrl?: string;
  isDeleted?: boolean;
  deletedAt?: string;
}

export interface BlogPayload {
  slug?: string;
  subtitle?: string;
  date?: string;
  readTime?: string;
  coverGradient?: string;
  body: Section[] | string;
  status?: BlogStatus;
  seoTitle?: string;
  seoDescription?: string;
  coverImageUrl?: string;
}

// ─── Service calls ────────────────────────────────────────────────────────────

export const fetchAllBlogs = (): Promise<Blog[]> =>
  apiFetch<Blog[]>('/aurel/blogs/all');

export const fetchPublishedBlogs = (): Promise<Blog[]> =>
  apiFetch<Blog[]>('/aurel/blogs/published');

export const fetchTrashBlogs = (): Promise<Blog[]> =>
  apiFetch<Blog[]>('/aurel/blogs/trash');

export const fetchBlogById = (id: string | number): Promise<Blog> =>
  apiFetch<Blog>(`/aurel/blogs/get/${id}`);

export const fetchBlogBySlug = (slug: string): Promise<Blog> =>
  apiFetch<Blog>(`/aurel/blogs/slug/${slug}`);

export const createBlog = (data: BlogPayload): Promise<{ insertId: number; message: string }> =>
  apiFetch<{ insertId: number; message: string }>('/aurel/blogs/add', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateBlog = (id: string | number, data: Partial<BlogPayload>): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/blogs/update/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const updateBlogStatus = (id: string | number, status: BlogStatus): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/blogs/status/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

export const deleteBlog = (id: string | number): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/blogs/delete/${id}`, {
    method: 'DELETE',
  });

export const restoreBlog = (id: string | number): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/blogs/restore/${id}`, {
    method: 'PATCH',
  });

export const purgeBlog = (id: string | number): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/blogs/purge/${id}`, {
    method: 'DELETE',
  });

export const uploadBlogCover = (file: File): Promise<{ fileKey: string; url: string }> => {
  const formData = new FormData();
  formData.append('blogImage', file);
  return apiFetch<{ fileKey: string; url: string }>('/aurel/blogs/upload-cover', {
    method: 'POST',
    body: formData,
  });
};
